/**
 * CampaignWizardPage — 3-step creation flow for /campaign-wizard.
 *
 * The server-side wizard has five steps (basics / targeting / payout /
 * caps / review). The operator-facing dash compresses these into three
 * logical UI steps:
 *   1. Name & offer       -> POST step=basics
 *   2. Targeting           -> POST step=targeting  (+ payout and caps
 *                                                    defaults under the hood)
 *   3. Review & submit     -> POST step=review, then POST /finalize
 *
 * Plain React state machine; no router navigation between steps; the
 * whole thing lives on one page.
 */
import { useCallback, useMemo, useState, type FormEvent } from "react";
import {
	DEFAULT_CAPS,
	DEFAULT_PAYOUT,
	validateCountries,
	validateName,
	validateOfferId,
	validateDescription,
	type BasicsPayload,
	type CampaignDraftRow,
	type TargetingPayload,
} from "./api";
import {
	useCreateCampaignDraft,
	useFinalizeCampaignDraft,
	useSaveCampaignStep,
} from "./hooks";

type UiStep = 1 | 2 | 3;

// Tailwind v4 classname helpers — kept as constants so the JSX stays scan-able.
const CARD = "rounded-lg border border-zinc-200 bg-white p-6 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const INPUT_ERR =
	"w-full rounded-md border border-red-400 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";
const BTN_PRIMARY =
	"rounded-md border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500/40 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-md border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:opacity-60";

interface BasicsForm {
	name: string;
	offer_id: string;
	description: string;
}

interface TargetingForm {
	countries: string;
}

interface BasicsErrors {
	name?: string;
	offer_id?: string;
	description?: string;
}

interface TargetingErrors {
	countries?: string;
}

function StepPill({ active, done, children }: {
	active: boolean;
	done: boolean;
	children: React.ReactNode;
}) {
	const base = "inline-flex items-center gap-2 rounded-md px-3 py-1 text-xs font-medium";
	if (active) return <span className={`${base} bg-sky-50 text-sky-700 ring-1 ring-sky-200`}>{children}</span>;
	if (done) return <span className={`${base} bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200`}>{children}</span>;
	return <span className={`${base} bg-zinc-50 text-zinc-500 ring-1 ring-zinc-200`}>{children}</span>;
}

export function CampaignWizardPage() {
	const createMut = useCreateCampaignDraft();
	const stepMut = useSaveCampaignStep();
	const finalizeMut = useFinalizeCampaignDraft();

	const [draft, setDraft] = useState<CampaignDraftRow | null>(null);
	const [step, setStep] = useState<UiStep>(1);
	const [completedCampaignId, setCompletedCampaignId] = useState<string | null>(null);

	const [basics, setBasics] = useState<BasicsForm>({
		name: "",
		offer_id: "",
		description: "",
	});
	const [targeting, setTargeting] = useState<TargetingForm>({ countries: "" });
	const [reviewConfirm, setReviewConfirm] = useState(false);

	const [basicsErrors, setBasicsErrors] = useState<BasicsErrors>({});
	const [targetingErrors, setTargetingErrors] = useState<TargetingErrors>({});

	const pendingAny =
		createMut.isPending || stepMut.isPending || finalizeMut.isPending;

	const mutationError = useMemo(() => {
		const err = createMut.error ?? stepMut.error ?? finalizeMut.error;
		return err instanceof Error ? err.message : null;
	}, [createMut.error, stepMut.error, finalizeMut.error]);

	// -------- step 1 ---------------------------------------------------------

	const validateBasics = useCallback((): BasicsPayload | null => {
		const errs: BasicsErrors = {};
		const name = validateName(basics.name);
		if (!name) errs.name = "Required, up to 120 characters.";
		const offerId = validateOfferId(basics.offer_id);
		if (!offerId) errs.offer_id = "Required.";
		let description: string | undefined;
		if (basics.description.trim().length > 0) {
			const normalized = validateDescription(basics.description);
			if (!normalized) errs.description = "Too long.";
			else description = normalized;
		}
		setBasicsErrors(errs);
		if (Object.keys(errs).length > 0 || !name || !offerId) return null;
		return description ? { name, offer_id: offerId, description } : { name, offer_id: offerId };
	}, [basics]);

	const onStep1Next = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			const payload = validateBasics();
			if (!payload) return;

			try {
				const d = draft ?? (await createMut.mutateAsync());
				if (!draft) setDraft(d);
				await stepMut.mutateAsync({
					id: d.id,
					step: "basics",
					payload,
				});
				setStep(2);
			} catch {
				/* mutation state carries the error */
			}
		},
		[basics, createMut, draft, stepMut, validateBasics],
	);

	// -------- step 2 ---------------------------------------------------------

	const validateTargeting = useCallback((): TargetingPayload | null => {
		const errs: TargetingErrors = {};
		const countries = validateCountries(targeting.countries);
		if (!countries) errs.countries = "At least one ISO country code.";
		setTargetingErrors(errs);
		return countries ? { countries } : null;
	}, [targeting]);

	const onStep2Next = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			if (!draft) return;
			const payload = validateTargeting();
			if (!payload) return;

			try {
				await stepMut.mutateAsync({
					id: draft.id,
					step: "targeting",
					payload,
				});
				// Post defaults for the hidden payout + caps steps so the API's
				// finalize contract accepts the draft on step 3 submit.
				await stepMut.mutateAsync({
					id: draft.id,
					step: "payout",
					payload: DEFAULT_PAYOUT,
				});
				await stepMut.mutateAsync({
					id: draft.id,
					step: "caps",
					payload: DEFAULT_CAPS,
				});
				setStep(3);
			} catch {
				/* mutation state carries the error */
			}
		},
		[draft, stepMut, validateTargeting],
	);

	// -------- step 3 ---------------------------------------------------------

	const onStep3Submit = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			if (!draft || !reviewConfirm) return;
			try {
				await stepMut.mutateAsync({
					id: draft.id,
					step: "review",
					payload: { confirmed: true },
				});
				const result = await finalizeMut.mutateAsync(draft.id);
				setCompletedCampaignId(result.campaign.id);
			} catch {
				/* mutation state carries the error */
			}
		},
		[draft, finalizeMut, reviewConfirm, stepMut],
	);

	// -------- back navigation -----------------------------------------------

	const onBack = useCallback(() => {
		setStep((s) => (s > 1 ? ((s - 1) as UiStep) : s));
	}, []);

	// -------- rendering ------------------------------------------------------

	if (completedCampaignId) {
		return (
			<main
				data-testid="campaign-wizard-page"
				className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-6 py-10"
			>
				<header className="flex flex-col gap-2">
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Campaign Wizard
					</h1>
					<p className="text-sm text-zinc-500">Draft finalized.</p>
				</header>
				<div className={CARD} data-testid="campaign-wizard-done">
					<p className="text-sm text-zinc-700">
						Campaign{" "}
						<code className="rounded bg-zinc-50 px-1.5 py-0.5 text-xs text-zinc-900 ring-1 ring-zinc-200">
							{completedCampaignId}
						</code>{" "}
						is now active.
					</p>
				</div>
			</main>
		);
	}

	return (
		<main
			data-testid="campaign-wizard-page"
			className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-3">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Campaign Wizard
				</h1>
				<p className="text-sm text-zinc-500">
					Name the campaign, pick your targeting, and review before going live.
				</p>
				<div
					className="mt-1 flex items-center gap-2"
					data-testid="campaign-wizard-step-indicator"
				>
					<StepPill active={step === 1} done={step > 1}>
						1. Name
					</StepPill>
					<span className="text-zinc-300">—</span>
					<StepPill active={step === 2} done={step > 2}>
						2. Targeting
					</StepPill>
					<span className="text-zinc-300">—</span>
					<StepPill active={step === 3} done={false}>
						3. Review
					</StepPill>
				</div>
			</header>

			{mutationError ? (
				<div
					role="alert"
					className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
					data-testid="campaign-wizard-error"
				>
					{mutationError}
				</div>
			) : null}

			{step === 1 ? (
				<form
					onSubmit={onStep1Next}
					className={`${CARD} flex flex-col gap-5`}
					data-testid="campaign-wizard-step-1"
				>
					<div className="flex flex-col gap-2">
						<label htmlFor="cw-name" className={LABEL}>
							Campaign name
						</label>
						<input
							id="cw-name"
							type="text"
							className={basicsErrors.name ? INPUT_ERR : INPUT}
							value={basics.name}
							placeholder="Summer 2026 push"
							onChange={(e) =>
								setBasics((prev) => ({ ...prev, name: e.currentTarget.value }))
							}
							data-testid="campaign-wizard-step-1-name"
						/>
						{basicsErrors.name ? (
							<p className="text-xs text-red-600">{basicsErrors.name}</p>
						) : null}
					</div>

					<div className="flex flex-col gap-2">
						<label htmlFor="cw-offer" className={LABEL}>
							Offer ID
						</label>
						<input
							id="cw-offer"
							type="text"
							className={basicsErrors.offer_id ? INPUT_ERR : INPUT}
							value={basics.offer_id}
							placeholder="ofr_…"
							onChange={(e) =>
								setBasics((prev) => ({
									...prev,
									offer_id: e.currentTarget.value,
								}))
							}
							data-testid="campaign-wizard-step-1-offer"
						/>
						{basicsErrors.offer_id ? (
							<p className="text-xs text-red-600">{basicsErrors.offer_id}</p>
						) : null}
					</div>

					<div className="flex flex-col gap-2">
						<label htmlFor="cw-desc" className={LABEL}>
							Notes <span className="text-zinc-400">(optional)</span>
						</label>
						<textarea
							id="cw-desc"
							rows={3}
							className={basicsErrors.description ? INPUT_ERR : INPUT}
							value={basics.description}
							onChange={(e) =>
								setBasics((prev) => ({
									...prev,
									description: e.currentTarget.value,
								}))
							}
							data-testid="campaign-wizard-step-1-desc"
						/>
						{basicsErrors.description ? (
							<p className="text-xs text-red-600">{basicsErrors.description}</p>
						) : null}
					</div>

					<div className="mt-2 flex items-center justify-end">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={pendingAny}
							data-testid="campaign-wizard-step-1-next"
						>
							{pendingAny ? "Saving…" : "Next: Targeting"}
						</button>
					</div>
				</form>
			) : null}

			{step === 2 ? (
				<form
					onSubmit={onStep2Next}
					className={`${CARD} flex flex-col gap-5`}
					data-testid="campaign-wizard-step-2"
				>
					<div className="flex flex-col gap-2">
						<label htmlFor="cw-countries" className={LABEL}>
							Countries
						</label>
						<textarea
							id="cw-countries"
							rows={3}
							placeholder="US, CA, GB"
							className={targetingErrors.countries ? INPUT_ERR : INPUT}
							value={targeting.countries}
							onChange={(e) =>
								setTargeting({ countries: e.currentTarget.value })
							}
							data-testid="campaign-wizard-step-2-countries"
						/>
						<p className="text-xs text-zinc-500">
							ISO-2 codes. Separate with commas or new lines.
						</p>
						{targetingErrors.countries ? (
							<p className="text-xs text-red-600">{targetingErrors.countries}</p>
						) : null}
					</div>

					<div className="mt-2 flex items-center justify-between">
						<button
							type="button"
							onClick={onBack}
							className={BTN_GHOST}
							disabled={pendingAny}
							data-testid="campaign-wizard-back"
						>
							Back
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={pendingAny}
							data-testid="campaign-wizard-step-2-next"
						>
							{pendingAny ? "Saving…" : "Next: Review"}
						</button>
					</div>
				</form>
			) : null}

			{step === 3 ? (
				<form
					onSubmit={onStep3Submit}
					className={`${CARD} flex flex-col gap-5`}
					data-testid="campaign-wizard-step-3"
				>
					<div className="flex flex-col gap-4">
						<h2 className="text-lg font-semibold text-zinc-900">Review</h2>
						<dl
							className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm"
							data-testid="campaign-wizard-summary"
						>
							<dt className="text-zinc-500">Name</dt>
							<dd className="text-zinc-900" data-testid="campaign-wizard-summary-name">
								{basics.name || "—"}
							</dd>

							<dt className="text-zinc-500">Offer</dt>
							<dd className="text-zinc-900" data-testid="campaign-wizard-summary-offer">
								{basics.offer_id || "—"}
							</dd>

							<dt className="text-zinc-500">Countries</dt>
							<dd className="text-zinc-900" data-testid="campaign-wizard-summary-countries">
								{(validateCountries(targeting.countries) ?? []).join(", ") || "—"}
							</dd>

							{basics.description.trim() ? (
								<>
									<dt className="text-zinc-500">Notes</dt>
									<dd className="text-zinc-900">{basics.description.trim()}</dd>
								</>
							) : null}
						</dl>

						<label className="mt-2 flex items-start gap-2 text-sm text-zinc-700">
							<input
								type="checkbox"
								className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500"
								checked={reviewConfirm}
								onChange={(e) => setReviewConfirm(e.currentTarget.checked)}
								data-testid="campaign-wizard-confirm"
							/>
							<span>I've reviewed the details above and want to launch this campaign.</span>
						</label>
					</div>

					<div className="mt-2 flex items-center justify-between">
						<button
							type="button"
							onClick={onBack}
							className={BTN_GHOST}
							disabled={pendingAny}
							data-testid="campaign-wizard-back"
						>
							Back
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={!reviewConfirm || pendingAny}
							data-testid="campaign-wizard-submit"
						>
							{pendingAny ? "Launching…" : "Launch campaign"}
						</button>
					</div>
				</form>
			) : null}
		</main>
	);
}

export default CampaignWizardPage;
